import type { GetStaticPaths, GetStaticProps } from 'next'
import type { FC } from 'react'
import type { Category, Post } from '@/types'

import Link from 'next/link'

import { Seo } from '@/components/generals/seo'
import { SiteLayout } from '@/components/layout/site-layout'
import { GoldCta } from '@/components/products'

import { BlogGridCard } from '@/components/blog'

import { getLayoutData } from '@/lib/layout-data'
import { getPostBySlug, getPosts, getRelatedPosts } from '@/lib/collections'

interface BlogPostProps {
    categories: Category[]
    post: Post
    related: Post[]
}

const BlogPost: FC<BlogPostProps> = ({ categories, post, related }) => {
    return (
        <SiteLayout categories={categories}>
            <Seo title={post.name} description={post.summary} ogImage={post.mainImage} />

            <div className='section blog-post'>
                <div className='container nav-ignore'>
                    <div className='animation-on-load-01'>
                        <div className='navigation-holder'>
                            <div>
                                <Link href='/' className='normal-accent-link'>HOME</Link> /{' '}
                                <Link href='/blog' id='Normal-Accent-Link' className='normal-accent-link'>BLOG</Link> /
                            </div>
                            <div className='caps'>{post.name}</div>
                        </div>
                    </div>
                    <div className='blog-border-holder'>
                        <div className='blog-border-container'>
                            <div className='blog-main-image-holder'>
                                <img alt={post.name} loading='lazy' src={post.mainImage} className='blog-main-image' />
                            </div>
                            <div className='blog-details-holder'>
                                <div className='animation-on-load-02'>
                                    <div className='blog-post-featured-details-container center-text'>
                                        <div className='fade-in-move-on-scroll'>
                                            <Link href={`/category/${post.categorySlug}`} className='w-inline-block'>
                                                <div className='category-and-time-container'>
                                                    <div className='blog-featured-category-text'>{post.category}</div>
                                                    <div className='blog-featured-category-text-grey'>•</div>
                                                    <div className='blog-featured-category-text-grey'>{post.readTime}</div>
                                                    <div className='blog-featured-category-text-grey'>mins read</div>
                                                </div>
                                            </Link>
                                        </div>
                                        <div className='fade-in-move-on-scroll'>
                                            <div className='featured-author-container normal'>
                                                <div className='author-container'>
                                                    <Link href={post.author ? `/author/${post.author.slug}` : '#'} className='author-profile-picture-holder w-inline-block'>
                                                        <div className='author-profile-picture-container'>
                                                            <img alt={post.author?.name ?? ''} loading='lazy' src={post.author?.picture ?? ''} className='author-profile-picture-image' />
                                                        </div>
                                                        <div className='author-details-holder'>
                                                            <div className='author-name'>{post.author?.name}</div>
                                                            <p className='paragraph-s grey-text'>{post.author?.role}</p>
                                                        </div>
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                        <div className='fade-in-move-on-scroll'>
                                            <h2>{post.name}</h2>
                                        </div>
                                        <div className='fade-in-move-on-scroll'>
                                            <p className='grey-text'>{post.summary}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='rich-text-holder'>
                        <div className='rich-text-container'>
                            <div className='fade-in-move-on-scroll'>
                                <div className='w-richtext' dangerouslySetInnerHTML={{ __html: post.body }} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className='section grey-section'>
                <div className='container'>
                    <div className='fade-in-move-on-scroll'>
                        <h3 className='accent-text-color'>Related Articles</h3>
                    </div>
                    <div className='fade-in-move-on-scroll'>
                        <div className='blog-grid-wrapper w-dyn-list'>
                            <div role='list' className='blog-grid w-dyn-items'>
                                {related.map((item) => (
                                    <BlogGridCard key={item.slug} post={item} />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <GoldCta />
        </SiteLayout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => ({
    paths: getPosts().map((post) => ({ params: { slug: post.slug } })),
    fallback: false
})

export const getStaticProps: GetStaticProps<BlogPostProps> = async ({ params }) => {
    const slug = params?.slug as string
    const post = getPostBySlug(slug)

    if (!post) {
        return { notFound: true }
    }

    const { categories } = getLayoutData()

    return {
        props: {
            categories,
            post,
            related: getRelatedPosts(slug)
        }
    }
}

export default BlogPost
